import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Button, Spin } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import DeliverySharePanel from "../../components/delivery/DeliverySharePanel";
import ExpiredPanel from "../../components/delivery/ExpiredPanel";
import ErrorPanel from "../../components/delivery/ErrorPanel";
import {
    buildDeliveryUrl,
    getAlbumName,
    getConsentClauseIds,
    hasConsent,
    isExpired,
} from "../../utils/deliveryToken";
import { getFolderDisplayName } from "../../utils/deliveryPath";
import { useDeliveryPayload } from "../../hooks/useDeliveryPayload";
import { useTranslation } from "../../i18n";
import "./delivery.css";

function DeliveryShare() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { status, payload } = useDeliveryPayload(token);
    const [expired, setExpired] = useState(false);
    const consented = useMemo(
        () => (payload && getConsentClauseIds(payload).length === 0) || hasConsent(token),
        [payload, token]
    );

    const deliveryUrl = useMemo(() => (token ? buildDeliveryUrl(token) : ""), [token]);
    const albumName = useMemo(() => {
        if (!payload) {
            return "";
        }
        return getAlbumName(payload, getFolderDisplayName(payload.i)) || t("delivery.actions.defaultAlbumName");
    }, [payload, t]);

    const expiresAtLabel = useMemo(() => {
        if (!payload?.e) {
            return "";
        }
        return dayjs(payload.e * 1000).format("YYYY-MM-DD HH:mm");
    }, [payload]);

    useEffect(() => {
        if (!payload) {
            setExpired(false);
            return;
        }
        setExpired(isExpired(payload.e));
    }, [payload]);

    useEffect(() => {
        const brand = "HEI CREATIVE";
        if (status === "loading") {
            document.title = `${t("delivery.actions.loadingAlbum")} · ${brand}`;
        } else if (status === "expired") {
            document.title = `${t("delivery.meta.expiredLink")} · ${brand}`;
        } else if (!payload) {
            document.title = `${t("delivery.meta.invalidLink")} · ${brand}`;
        } else if (expired) {
            document.title = `${t("delivery.meta.expiredLink")} · ${brand}`;
        } else {
            document.title = `${t("delivery.share.pageTitle")} · ${brand}`;
        }
    }, [status, payload, expired, t]);

    useEffect(() => {
        if (payload && !expired && !isExpired(payload.e) && !consented) {
            navigate(`/d/${token}`, { replace: true });
        }
    }, [payload, expired, consented, navigate, token]);

    const handleBack = useCallback(() => {
        navigate(`/d/${token}`);
    }, [navigate, token]);

    if (status === "loading") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <div className="delivery-browser delivery-browser--centered">
                        <Spin size="large" />
                        <p>{t("delivery.actions.loadingAlbum")}</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (status === "expired") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ExpiredPanel archiveMarkdown={payload?.archiveMarkdown} />
                </div>
                <Footer />
            </>
        );
    }

    if (!payload) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ErrorPanel />
                </div>
                <Footer />
            </>
        );
    }

    if (expired || isExpired(payload.e)) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ExpiredPanel archiveMarkdown={payload?.archiveMarkdown} />
                </div>
                <Footer />
            </>
        );
    }

    if (!consented) {
        return null;
    }

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <div className="delivery-page">
                <div className="delivery-share-page-bar">
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack}>
                        {t("delivery.share.backToGallery")}
                    </Button>
                </div>
                <DeliverySharePanel
                    url={deliveryUrl}
                    albumName={albumName}
                    expiresAtLabel={expiresAtLabel}
                />
            </div>
            <Footer />
        </>
    );
}

export default DeliveryShare;
